import React, { useState } from 'react';
import { Title, Text, Button, Stack, TextInput, Modal, Group, Paper } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconBuilding, IconPlus, IconUsers, IconShieldCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { createOrganization, regenerateCompanyToken } from '@mygpt/app';
import { useLogin } from '@/hooks/useLogin';
import { useAdminStore } from '@/store/admin';
import { useGPTStore } from '@/store';

interface EnableOrganizationViewProps {
  onOrganizationCreated?: () => void;
}

export const EnableOrganizationView: React.FC<EnableOrganizationViewProps> = ({ onOrganizationCreated }) => {
  const { t } = useTranslation();
  const { fetchCompanyId } = useAdminStore();
  const { updateToken } = useLogin();
  const [modalOpened, setModalOpened] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const handleCreateOrganization = async () => {
    if (!orgName.trim()) {
      setNameError(t('adminConsole.enableOrg.nameRequired'));
      return;
    }
    setLoading(true);
    setNameError('');
    try {
      const res = await createOrganization({ companyName: orgName.trim() });
      const resData = res?.data ?? res;
      if (resData?.success === false) {
        showNotification({ color: 'red', message: t('adminConsole.enableOrg.createFailed') });
        return;
      }
      // 创建企业后重新生成token，使token包含企业信息
      const companyId = resData?.company?.id;
      if (companyId) {
        const tokenRes = await regenerateCompanyToken(companyId);
        if (tokenRes.data?.access_token) {
          updateToken(tokenRes.data);
        }
      }
      await fetchCompanyId();
      // 在回调前设置assetScope，避免layout重渲染时Bots页面用个人模式查询
      const newCompanyId = useAdminStore.getState().companyId;
      if (newCompanyId) {
        useGPTStore.getState().setAssetScope(newCompanyId);
      }
      setModalOpened(false);
      setOrgName('');
      onOrganizationCreated?.();
    } catch (err: any) {
      showNotification({ color: 'red', message: t('adminConsole.enableOrg.createFailed') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-full">
      <Paper radius="lg" className="max-w-md w-full border border-gray-200 p-10">
        <Stack align="center" gap="md">
          <div className="w-14 h-14 rounded-xl bg-violet-50 flex items-center justify-center">
            <IconBuilding size={26} stroke={1.5} color="var(--mantine-color-violet-6)" />
          </div>

          <Stack align="center" gap={4}>
            <Title order={4} ta="center">
              {t('adminConsole.enableOrg.title')}
            </Title>
            <Text c="dimmed" ta="center" size="sm">
              {t('adminConsole.enableOrg.description')}
            </Text>
          </Stack>

          <Stack gap={8} className="w-full">
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-gray-50">
              <IconUsers size={16} stroke={1.5} color="var(--mantine-color-violet-5)" className="flex-shrink-0" />
              <Text size="xs" c="gray.6">
                {t('adminConsole.enableOrg.featureTeam')}
              </Text>
            </div>
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-gray-50">
              <IconShieldCheck size={16} stroke={1.5} color="var(--mantine-color-violet-5)" className="flex-shrink-0" />
              <Text size="xs" c="gray.6">
                {t('adminConsole.enableOrg.featureResource')}
              </Text>
            </div>
          </Stack>

          <Button
            color="violet"
            radius="md"
            fullWidth
            leftSection={<IconPlus size={16} />}
            onClick={() => setModalOpened(true)}
          >
            {t('adminConsole.enableOrg.createButton')}
          </Button>
        </Stack>
      </Paper>

      <Modal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setNameError('');
        }}
        title={t('adminConsole.enableOrg.modalTitle')}
        centered
      >
        <Stack>
          <TextInput
            label={t('adminConsole.enableOrg.orgName')}
            placeholder={t('adminConsole.enableOrg.orgNamePlaceholder')}
            value={orgName}
            onChange={(e) => {
              setOrgName(e.currentTarget.value);
              if (nameError) setNameError('');
            }}
            required
            error={nameError || undefined}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpened(false)}>
              {t('common.cancel', '取消')}
            </Button>
            <Button color="violet" onClick={handleCreateOrganization} loading={loading}>
              {t('common.create', '创建')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default EnableOrganizationView;
